
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import ImageUpload from '@/components/shared/ImageUpload'

interface Testimonial {
  id: string
  name: string
  position: string
  company: string | null
  testimonial: string
  rating: number
  image_url: string | null
  display_order: number
  is_active: boolean
}

interface TestimonialFormProps {
  testimonial?: Testimonial | null
  onSave: () => void
  onCancel: () => void
}

const TestimonialForm = ({ testimonial, onSave, onCancel }: TestimonialFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    company: '',
    testimonial: '',
    rating: 5,
    image_url: '',
    display_order: 0,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (testimonial) {
      setFormData({
        name: testimonial.name,
        position: testimonial.position,
        company: testimonial.company || '',
        testimonial: testimonial.testimonial,
        rating: testimonial.rating,
        image_url: testimonial.image_url || '',
        display_order: testimonial.display_order,
        is_active: testimonial.is_active
      })
    }
  }, [testimonial])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const testimonialData = {
        name: formData.name,
        position: formData.position,
        company: formData.company || null,
        testimonial: formData.testimonial,
        rating: formData.rating,
        image_url: formData.image_url || null,
        display_order: formData.display_order,
        is_active: formData.is_active
      }

      if (testimonial) {
        const { error } = await supabase
          .from('testimonials')
          .update(testimonialData)
          .eq('id', testimonial.id)
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Testimonial updated successfully",
        })
      } else {
        const { error } = await supabase
          .from('testimonials')
          .insert([testimonialData])
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Testimonial created successfully",
        })
      }
      
      onSave()
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${testimonial ? 'update' : 'create'} testimonial`,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{testimonial ? 'Edit Testimonial' : 'Add New Testimonial'}</CardTitle>
        <CardDescription>
          {testimonial ? 'Update testimonial information' : 'Create a new testimonial entry'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="position">Position</Label>
            <Input
              id="position"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="testimonial">Testimonial</Label>
            <Textarea
              id="testimonial"
              value={formData.testimonial}
              onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="rating">Rating (1-5)</Label>
            <Input
              id="rating"
              type="number"
              min="1"
              max="5"
              value={formData.rating}
              onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) || 5 })}
            />
          </div>

          <ImageUpload
            currentImageUrl={formData.image_url}
            onImageUpload={(url) => setFormData({ ...formData, image_url: url })}
            onImageRemove={() => setFormData({ ...formData, image_url: '' })}
            label="Client Photo"
            folder="testimonials"
          />
          
          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : (testimonial ? 'Update Testimonial' : 'Create Testimonial')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default TestimonialForm
